import { camelCaseTransformer } from '@sondermind/utilities/tools';

import { IFinancialAmount } from '@sondermind/core/models/billing/financial-amount.model';

export interface ITempImprovementPatientInsurances {
  id: number;
  patientId?: number;
  subscriberRelationship: string;
  deleted?: boolean;
  createdAt: Date;
  updatedAt: Date;
  overrideBenefits?: boolean;
  overrideType: string;
  overrideAmount?: {
    amount: number;
    amountS: string;
  };
  personaClientId: number;
  cardVerified?: boolean;
  requiresCardVerification?: boolean;
  addedByAdmin: boolean;
  usedRedemptions?: number;
  maxRedemptions?: number;
  personaProviderId?: string;
  insuranceInformationId: string;
  informationVerified?: boolean;
  informationVerifiedChangedAt?: Date;
  parentInsuranceId?: number;
  eapSessionsRemaining?: number;
  eapSchedulableSessionsRemaining?: number;

  // only included in views:
  // "with_provider_network_stub", "for_early_by", "with_provider_network_detail", "with_insurance_information"
  providerNetworkCompany?: {
    id: number;
    eap?: boolean;
    authNumberVisible?: boolean;
    authNumberRequired?: boolean;
    veteransAffairs?: boolean;
    redemption: boolean;
    name: string;
    networkState: string;
    isCashPay?: boolean;
    isUsageBased?: boolean;
    isProBono?: boolean;
    medicare?: boolean;
    medicareAdvantage?: boolean;
    averageClientResponsibility?: number;
    providersCanAddToClients?: boolean;
    skipEarlyBv?: boolean;
  };

  // only included in views:
  // "for_early_by", "with_insurance_information"
  personaClient?: {
    id: number;
    displayName: string;
    fullName: string;
    sessionInScheduledStatus: boolean;
  };

  //  only included in views:
  // "with_provider_network_detail", "with_insurance_information"
  insuranceInformation?: {
    id: string;
    patientInsuranceId: number;
    providerNetworkCompanyId: number;
    groupNumber: string;
    policyNumber: string;
    groupPlanName: string;
    phoneNumber: string;
    firstName: string;
    middleName: string;
    lastName: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    postalCode: string;
    payerName: string;
    payerAddressLine1: string;
    payerAddressLine2: string;
    payerCity: string;
    payerState: string;
    payerZip: string;
    payerPhone: string;
    eapCompanyName: string;
    payerId: string;
    authNumber: string;
    authNotes: string;
    dateOfBirth: string;
    dependent?: boolean;
    relationshipToInsuredId?: number;
    genderId?: number;
    createdAt: Date;
    updatedAt: Date;
    ranking?: number;
    medicareTypeId: string;
    eapSessionCount?: number;
    eapStartDate: string | Date;
    eapEndDate: string | Date;
  };

  // only included in view:
  // "with_insurance_information"
  frontCardPicture?: {
    mediumUrl: string;
    originalUrl: string;
  };
  backCardPicture?: {
    mediumUrl: string;
    originalUrl: string;
  };
  lastBenefitVerification?: {
    response: string;
  };

  benefitVerification?: {
    id: number;
    aasmState: string;
    createdAt: string;
    updatedAt: string;
    verified: boolean;
    coverageStartDate?: string;
    coverageEndDate?: string;
  };

  // FE only property
  remainingRedemptions?: number;
  canEdit?: boolean;
  insuranceRank?: string;
  sessionStatus?: InsuranceStateType | null;
  sessionStatusText?: string;
  expirationStatus?: InsuranceStateType | null;
  expirationStatusText?: string;
  showExpirationDate?: boolean;
  coverageVerificationStatus?: InsuranceStateType | null;
  coverageVerificationStatusText?: string;
}

export function convertTempImprovedPatientInsurance(json: any): ITempImprovementPatientInsurances {
  const transformed = camelCaseTransformer<ITempImprovementPatientInsurances>(json);

  transformed.updatedAt = new Date(transformed.updatedAt);
  transformed.createdAt = new Date(transformed.createdAt);
  transformed.informationVerifiedChangedAt = new Date(transformed.informationVerifiedChangedAt);

  if (transformed.insuranceInformation) {
    transformed.insuranceInformation.createdAt = new Date(transformed.insuranceInformation.createdAt);
    transformed.insuranceInformation.updatedAt = new Date(transformed.insuranceInformation.updatedAt);

    if (transformed.insuranceInformation.ranking == null) {
      transformed.insuranceRank = 'In Review';
    } else if (transformed.insuranceInformation.ranking === 1) {
      transformed.insuranceRank = 'Primary';
    } else if (transformed.insuranceInformation.ranking > 1) {
      transformed.insuranceRank = 'Secondary';
    }
  }

  if (transformed.providerNetworkCompany?.isUsageBased && transformed.maxRedemptions && transformed.usedRedemptions) {
    transformed.remainingRedemptions = transformed.maxRedemptions - transformed.usedRedemptions;
  }

  transformed.canEdit = !transformed.deleted && transformed?.providerNetworkCompany?.providersCanAddToClients;

  return transformed;
}

export enum CoverageType {
  COPAY_NO_DEDUCTIBLE_NO_OOP_MAX = 'Copay, no deductible, no OOP Max',
  COPAY_NO_DEDUCTIBLE_WITH_OOP_MAX = 'Copay, no deductible, with OOP Max',
  COPAY_AFTER_DEDUCTIBLE_NO_OOP_MAX = 'Copay after deductible, no OOP Max',
  COPAY_AFTER_DEDUCTIBLE_WITH_OOP_MAX = 'Copay after deductible, with OOP Max',
  COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX = 'Coinsurance after deductible, no OOP Max',
  COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX = 'Coinsurance after deductible, with OOP Max',
  COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX = 'Coinsurance, no deductible, with OOP Max',
  COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX = 'Coinsurance, no deductible, no OOP Max',
  COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX = 'Copay and coinsurance, no deductible, with OOP Max',
  COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX = 'Copay and coinsurance, no deductible, no OOP Max',
  COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX = 'Copay and coinsurance after deductible, no OOP Max',
  COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX = 'Copay and coinsurance after deductible, with OOP Max',
  FULLY_COVERED_AFTER_DEDUCTIBLE = 'Fully covered after deductible',
  FULLY_COVERED = 'Fully covered',
  UNKNOWN = 'Unknown'
}

export interface IBenefitsInformation {
  id: number;
  costEstimate: IFinancialAmount;
  telehealthCostEstimate: IFinancialAmount;
  coPayAmount: IFinancialAmount;
  telehealthCoPayAmount: IFinancialAmount;
  coInsurancePercent: IFinancialAmount;
  telehealthCoInsurancePercent: IFinancialAmount;
  expectedInsurancePayment: IFinancialAmount;
  indDeductibleLimit: IFinancialAmount;
  indDeductibleRemaining: IFinancialAmount;
  indOutOfPocketLimit: IFinancialAmount;
  indOutOfPocketRemaining: IFinancialAmount;
  failureReason: string;
  displayCostEstimate: boolean;
  telehealth: boolean;
  benefitStructure: IBenefitStructure;
  benefitStructureConfig?: IBenefitStructureConfig;

  // FE only properties
  displayedClientCostEstimate: number;
  displayedClientCoPayAmount: number;
  displayedClientCoInsurancePercent: number;
}

export function convertLastBenefitInformation(json: any): IBenefitsInformation {
  const converted = camelCaseTransformer<IBenefitsInformation>(json);

  if (converted.telehealth) {
    converted.displayedClientCostEstimate = converted.telehealthCostEstimate?.amount;
    converted.displayedClientCoPayAmount = converted.telehealthCoPayAmount?.amount;
    converted.displayedClientCoInsurancePercent = converted.telehealthCoInsurancePercent?.amount;
  } else {
    converted.displayedClientCostEstimate = converted.costEstimate?.amount;
    converted.displayedClientCoPayAmount = converted.coPayAmount?.amount;
    converted.displayedClientCoInsurancePercent = converted.coInsurancePercent?.amount;
  }

  return converted;
}

/**
 * Parses a date string as local date to avoid timezone issues
 */
export function parseLocalDate(dateString: string): Date {
  // Parse the date string as local date to avoid UTC timezone issues
  const parts = dateString.split('-');
  if (parts.length === 3) {
    // Create date with local timezone (month is 0-indexed)
    const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return date;
  }
  // Fallback to regular Date parsing if format is unexpected
  return new Date(dateString);
}

/**
 * Gets today's date as a local date with time components cleared
 * This ensures consistent date creation matching parseLocalDate behavior
 */
export function getTodayAsLocalDate(): Date {
  const now = new Date();
  // Create a new date using the same pattern as parseLocalDate to ensure consistency
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today;
}

export interface IBenefitStructure {
  id: number;
  name: string;
  usesCopay: boolean;
  usesCoinsurance: boolean;
  hasDeductible: boolean;
  hasOopMax: boolean;
}

export interface IBenefitStructureConfig {
  id: number;
  displayCostEstimate: boolean;
  enableEarlyCharging: boolean;
}

export enum InsuranceState {
  EXPIRED = 'Expired',
  EXPIRING_SOON = 'Expiring soon',
  OUT_OF_SESSIONS = 'Out of sessions',
  COVERAGE_NOT_STARTED = 'Coverage not started',
  UNABLE_TO_VERIFY = 'Unable to verify',
}

export enum InsuranceStateType {
  WARNING = 'warning',
  ALERT = 'alert'
}

export enum InsuranceActionType {
  ADD_INSURANCE = 'add_insurance',
  CONTINUE_WITH_SELF_PAY = 'continue_with_self_pay'
}
